import GalleryImagePickerController from "./gallery_image_picker_controller"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Heading from "@tiptap/extension-heading"
import Link from "@tiptap/extension-link"
import HorizontalRule from "@tiptap/extension-horizontal-rule"
import Placeholder from "@tiptap/extension-placeholder"
import Image from "@tiptap/extension-image"
import Underline from "@tiptap/extension-underline"

/**
 * Rich text editor powered by Tiptap.
 * Inherits from GalleryImagePickerController to support image insertion from the gallery.
 * Handles content synchronization with a hidden input field.
 */
export default class extends GalleryImagePickerController {
  static targets = [
    "imagePicker",
    "imageList",
    "imageUploadInput",
    "imageFirstButton",
    "imagePrevButton",
    "imageNextButton",
    "imageLastButton",
    "imagePaginationStatus",
    "paginationNav",
    "searchInput",
    "editor",
    "input",
    "boldButton",
    "italicButton",
    "underlineButton",
    "linkButton",
    "imageButton",
    "heading2Button",
    "heading3Button",
    "horizontalRuleButton",
    "bulletListButton",
    "orderedListButton",
    "undoButton",
    "redoButton"
  ]

  static values = {
    placeholder: String,
    minHeightClass: { type: String, default: "min-h-48" },
    loadingMessage: String,
    errorLoadingMessage: String,
    emptyMessage: String,
    uploadErrorMessage: String,
    linkPromptLabel: String,
    linkPromptPlaceholder: String,
    paginationLabel: String,
    paginationInfoTemplate: String,
    firstPageLabel: String,
    previousPageLabel: String,
    nextPageLabel: String,
    lastPageLabel: String
  }

  connect() {
    super.connect()

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          codeBlock: false,
          horizontalRule: false,
          strike: false,
          code: false
        }),
        Heading.configure({ levels: [2, 3] }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer"
          }
        }),
        HorizontalRule,
        Placeholder.configure({ placeholder: this.placeholderValue || "" }),
        Image.configure({ inline: false, allowBase64: false }),
        Underline
      ],
      content: this.inputTarget.value || "",
      editorProps: {
        attributes: {
          class: `${this.minHeightClassValue} tiptap-content focus:outline-none`,
          "data-tiptap-editor": "true"
        }
      },
      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.getHTML()
        this.refreshToolbar()
      },
      onSelectionUpdate: () => this.refreshToolbar(),
      onTransaction: () => this.refreshToolbar()
    })

    this.refreshToolbar()
  }

  disconnect() {
    super.disconnect()
    this.editor?.destroy()
  }

  /**
   * Toolbar actions for formatting and history.
   */
  toggleBold(event) { event.preventDefault(); this.editor.chain().focus().toggleBold().run(); this.refreshToolbar() }
  toggleItalic(event) { event.preventDefault(); this.editor.chain().focus().toggleItalic().run(); this.refreshToolbar() }
  toggleUnderline(event) { event.preventDefault(); this.editor.chain().focus().toggleUnderline().run(); this.refreshToolbar() }
  toggleHeading2(event) { event.preventDefault(); this.editor.chain().focus().toggleHeading({ level: 2 }).run(); this.refreshToolbar() }
  toggleHeading3(event) { event.preventDefault(); this.editor.chain().focus().toggleHeading({ level: 3 }).run(); this.refreshToolbar() }
  insertHorizontalRule(event) { event.preventDefault(); this.editor.chain().focus().setHorizontalRule().run(); this.refreshToolbar() }
  toggleBulletList(event) { event.preventDefault(); this.editor.chain().focus().toggleBulletList().run(); this.refreshToolbar() }
  toggleOrderedList(event) { event.preventDefault(); this.editor.chain().focus().toggleOrderedList().run(); this.refreshToolbar() }
  undo(event) { event.preventDefault(); this.editor.chain().focus().undo().run(); this.refreshToolbar() }
  redo(event) { event.preventDefault(); this.editor.chain().focus().redo().run(); this.refreshToolbar() }

  /**
   * Prompts the user for a URL and applies a link to the selected text.
   */
  setLink(event) {
    event.preventDefault()
    const previousUrl = this.editor.getAttributes("link").href
    const url = window.prompt(this.linkPromptLabelValue || "URL", previousUrl || this.linkPromptPlaceholderValue || "https://")
    if (url === null) return
    if (url.trim() === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
      this.refreshToolbar()
      return
    }
    this.editor.chain().focus().extendMarkRange("link").setLink({ href: this.normalizeUrl(url) }).run()
    this.refreshToolbar()
  }

  /**
   * Tiptap integration for selected images.
   */
  selectedImageValue() {
    return this.inputTarget.value
  }

  onImageSelect(image) {
    this.insertImage(image)
  }

  afterUploadImage(image) {
    this.insertImage(image)
  }

  /**
   * Inserts an image node into the editor.
   */
  insertImage(image) {
    this.abortRequest()
    this.editor.chain().focus().setImage({
      src: image.url,
      alt: image.alt || "",
      title: image.title || ""
    }).run()

    this.inputTarget.value = this.editor.getHTML()
    this.imagePickerTarget.classList.add("hidden")
  }

  /**
   * Basic URL normalization to ensure a protocol is present.
   */
  normalizeUrl(url) {
    const trimmedUrl = url.trim()
    return trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://") ? trimmedUrl : `https://${trimmedUrl}`
  }

  /**
   * Updates toolbar button states based on the current editor state.
   */
  refreshToolbar() {
    if (!this.editor) return
    this.toggleActive(this.boldButtonTarget, this.editor.isActive("bold"))
    this.toggleActive(this.italicButtonTarget, this.editor.isActive("italic"))
    this.toggleActive(this.underlineButtonTarget, this.editor.isActive("underline"))
    this.toggleActive(this.heading2ButtonTarget, this.editor.isActive("heading", { level: 2 }))
    this.toggleActive(this.heading3ButtonTarget, this.editor.isActive("heading", { level: 3 }))
    this.toggleActive(this.linkButtonTarget, this.editor.isActive("link"))
    this.toggleActive(this.bulletListButtonTarget, this.editor.isActive("bulletList"))
    this.toggleActive(this.orderedListButtonTarget, this.editor.isActive("orderedList"))
    this.undoButtonTarget.disabled = !this.editor.can().undo()
    this.redoButtonTarget.disabled = !this.editor.can().redo()
  }

  /**
   * Toggles CSS classes for active state on toolbar buttons.
   */
  toggleActive(button, active) {
    button.classList.toggle("bg-slate-900", active)
    button.classList.toggle("text-white", active)
    button.classList.toggle("border-slate-900", active)
    button.setAttribute("aria-pressed", active ? "true" : "false")
  }
}
